import * as THREE from 'three';
import { CAMERA, NAV, WALK } from '../config.js';

/**
 * 表示中の空間（スプラット or メッシュ）を、採寸・歩行・当たり判定から
 * 同じ形で扱うための窓口。
 *
 * - raycast(raycaster, hits)  … 採寸のクリック判定と、歩行の予備判定に使う
 * - forEachSurfacePoint(cb)   … 当たり判定マップ作成用。表面上の点をワールド座標で渡す。
 *                               cb(x, y, z, up) の up は面の法線の上向き成分（-1〜1）
 *                               （スプラットは面の向きが分からないので undefined）
 *                               メッシュは5番目の引数に、その点のテクスチャの色（0xRRGGBB）も渡す
 * - navOrigin / navRadius     … 当たり判定マップを作る中心と範囲
 * - floorSearchTop            … 床はこの高さより下から探す
 * - floorPeakRatio            … 床とみなす面が全体の点に占める最低割合
 * - nearClip                  … 歩行中のカメラの近接クリップ
 */

/** Gaussian Splatting のスキャン */
export function createSplatSource(mesh) {
  const origin = mesh.localToWorld(new THREE.Vector3(0, 0, 0));
  return Object.freeze({
    kind: 'splat',
    object: mesh,
    raycast: (raycaster, hits) => mesh.raycast(raycaster, hits),
    forEachSurfacePoint(cb) {
      const source = mesh.packedSplats?.lodSplats ?? mesh.packedSplats ?? mesh.splats;
      if (typeof source?.forEachSplat !== 'function') return;
      mesh.updateMatrixWorld(true);
      const matrix = mesh.matrixWorld;
      const p = new THREE.Vector3();
      source.forEachSplat((_, center, scales, __, opacity) => {
        // 薄い点・大きくぼやけた点は空中のゴミであることが多いので使わない
        if (opacity < NAV.minOpacity) return;
        if (Math.max(scales.x, scales.y, scales.z) > NAV.maxSplatScale) return;
        p.copy(center).applyMatrix4(matrix);
        cb(p.x, p.y, p.z, undefined);
      });
    },
    // スプラットは撮影開始地点（スキャン座標の原点）が部屋の中にある
    navOrigin: origin,
    navRadius: NAV.radius,
    // 撮影者の胸の高さより下を床の候補にする
    floorSearchTop: origin.y - NAV.minEyeHeight,
    floorPeakRatio: NAV.floorPeakRatio,
    // 三人称カメラの目の前に来る空中のゴミを消すため、手前を切る
    nearClip: WALK.nearClip,
  });
}

/** メッシュ（USDZ / GLB）のスキャン */
export function createModelSource(root, { navOrigin, navRadius = NAV.meshRadius, sampleDensity = NAV.meshSampleDensity }) {
  const box = new THREE.Box3().setFromObject(root);
  return Object.freeze({
    kind: 'model',
    object: root,
    raycast(raycaster, hits) {
      // 消した家具（非表示）は当たらないようにする。three のレイは非表示でも当たるため自分で外す
      hits.push(...raycaster.intersectObject(root, true).filter((hit) => isVisible(hit.object, root)));
    },
    forEachSurfacePoint(cb) {
      sampleSurface(root, sampleDensity, cb);
    },
    navOrigin,
    navRadius,
    floorSearchTop: box.max.y,
    // メッシュは壁や天井の面積が大きく、床の点の割合が小さくなる（GK702 で約5%）
    floorPeakRatio: NAV.meshFloorPeakRatio,
    // メッシュには空中のゴミが無い。手前を切ると横の壁が透けるので通常どおりにする
    nearClip: CAMERA.near,
  });
}

/**
 * メッシュの表面に面積比例で点をばらまく。
 * スプラットと同じ当たり判定マップの仕組み（マスごとの点の密度）に載せるため。
 * 乱数は固定シードにして、読み込むたびに判定が変わらないようにする。
 */
function sampleSurface(root, density, cb) {
  let seed = 1234567;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const normal = new THREE.Vector3();

  root.updateMatrixWorld(true);
  // traverseVisible は非表示の部品とその中身を飛ばす。消した家具が壁のように残らないようにするため
  root.traverseVisible((node) => {
    if (!node.isMesh) return;
    const colorAt = createColorSampler(node);
    const position = node.geometry.attributes.position;
    const index = node.geometry.index;
    const triangles = (index ? index.count : position.count) / 3;
    const vertex = (i) => (index ? index.getX(i) : i);

    for (let t = 0; t < triangles; t += 1) {
      a.fromBufferAttribute(position, vertex(t * 3)).applyMatrix4(node.matrixWorld);
      b.fromBufferAttribute(position, vertex(t * 3 + 1)).applyMatrix4(node.matrixWorld);
      c.fromBufferAttribute(position, vertex(t * 3 + 2)).applyMatrix4(node.matrixWorld);
      ab.subVectors(b, a);
      ac.subVectors(c, a);
      normal.crossVectors(ab, ac);
      const doubleArea = normal.length();
      if (doubleArea === 0) continue;
      const up = normal.y / doubleArea;

      // 期待値が density * 面積 になるよう、端数は確率で切り上げる
      const expected = (density * doubleArea) / 2;
      let count = Math.floor(expected);
      if (random() < expected - count) count += 1;

      for (let s = 0; s < count; s += 1) {
        let u = random();
        let v = random();
        if (u + v > 1) {
          u = 1 - u;
          v = 1 - v;
        }
        const i0 = vertex(t * 3);
        const i1 = vertex(t * 3 + 1);
        const i2 = vertex(t * 3 + 2);
        cb(a.x + ab.x * u + ac.x * v, a.y + ab.y * u + ac.y * v, a.z + ab.z * u + ac.z * v, up, colorAt(i0, i1, i2, u, v));
      }
    }
  });
}

/**
 * 三角形の中の点 (u, v) の色を、テクスチャから読む関数を作る。
 * テクスチャが読めなければ材質の色を返す。大きなテクスチャは縮小して読む（メモリ節約）。
 */
function createColorSampler(node) {
  const material = Array.isArray(node.material) ? node.material[0] : node.material;
  const fallback = material?.color ? material.color.getHex() : NO_COLOR;
  const uv = node.geometry.attributes.uv;
  const texture = material?.map;
  const image = texture?.image;
  if (!uv || !image || typeof document === 'undefined') return () => fallback;

  let pixels;
  let width;
  let height;
  try {
    const scale = Math.min(1, TEXTURE_SAMPLE_SIZE / Math.max(image.width, image.height));
    width = Math.max(1, Math.round(image.width * scale));
    height = Math.max(1, Math.round(image.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    pixels = context.getImageData(0, 0, width, height).data;
  } catch {
    return () => fallback;
  }

  const flipY = texture.flipY !== false;
  const fract = (n) => n - Math.floor(n);
  return (i0, i1, i2, u, v) => {
    const s = uv.getX(i0) + (uv.getX(i1) - uv.getX(i0)) * u + (uv.getX(i2) - uv.getX(i0)) * v;
    const t = uv.getY(i0) + (uv.getY(i1) - uv.getY(i0)) * u + (uv.getY(i2) - uv.getY(i0)) * v;
    const px = Math.min(width - 1, Math.floor(fract(s) * width));
    const tv = fract(t);
    const py = Math.min(height - 1, Math.floor((flipY ? 1 - tv : tv) * height));
    const o = (py * width + px) * 4;
    return (pixels[o] << 16) | (pixels[o + 1] << 8) | pixels[o + 2];
  };
}

/** テクスチャを読むときの最大の辺(px) */
const TEXTURE_SAMPLE_SIZE = 1024;
/** 色が分からない点 */
const NO_COLOR = 0xbbbbbb;

/** node からその空間の根までたどって、全部表示されているか */
function isVisible(node, root) {
  for (let current = node; current !== null && current !== root.parent; current = current.parent) {
    if (current.visible === false) return false;
  }
  return true;
}
